import React, {
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ThankYouWrapper } from '@/core/components/ThankYouWrapper';
import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import {
  ErrorsFields,
  FormState,
  SignupDetails,
  SignupFormPayload,
} from '@/core/interfaces/common';

import { SignupFormContainer } from '@/features/NewForm/SignupFormContainer';

import { request } from '@/utils/request';
import { handleCreateCustomFieldsParams } from '@/utils/helpers';

import {
  EmailThankYouMessage,
  GeneralErrorMessage,
} from '@/themes/globalStyles';

import { WidgetWrapper } from './SignupFormWidget.styles';

export interface SubmitResponse {
  message?: string;
  status?: string;
}

export interface SignupFormPageProps {
  signupDetails: SignupDetails;
}

export const SignupFormWidget = ({
  signupDetails,
}: SignupFormPageProps) => {
  const router = useRouter();
  const { uuid } = router.query;

  const {
    headlineConfig,
    isDonateButton,
    linkDonate,
    optIn,
    status,
    socialShareButtonEnabled,
    thankYouMessage,
    submitButtonText,
  } = signupDetails;

  const [
    formState,
    setFormState,
  ] = useState<FormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    zipCode: '',
  });
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(false);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<ErrorsFields>({});

  useEffect(() => {
    const formCustomFields = signupDetails.customFields.map(customField => ({
      ...customField,
      value: '',
    }));

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
    }));
  }, [signupDetails]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);
    const {
      customFields,
      ...restFormState
    } = formState;

    const params: SignupFormPayload = {
      ...restFormState,
      ...(customFields ?
        { customFields: handleCreateCustomFieldsParams(formState.customFields) } :
        {}),
    };

    return request
      .post<SubmitResponse>(`/public/signup/${uuid}/`, params)
      .then(({ data }) => {
        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        }
        setIsActionTaken(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          setError(data);
        }
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  if (isActionTaken) {
    return (
      <ThankYouWrapper
        buttonColor={headlineConfig?.buttonColor || '#2979FF'}
        isDonateButton={isDonateButton}
        linkDonate={linkDonate}
        name={error?.submitError || 'Submission complete!'}
        socialShareButtonEnabled={socialShareButtonEnabled}
        textcolor={headlineConfig?.textColor || '#000'}
        uuid={uuid}
      >
        {!error?.submitError && thankYouMessage && (
          <EmailThankYouMessage
            // eslint-disable-next-line react/no-danger
            centered
            dangerouslySetInnerHTML={{
              __html: thankYouMessage,
            }}
          />
        )}
      </ThankYouWrapper>
    );
  }

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Signup has been stopped."
      />
    );
  }

  return (
    <WidgetWrapper className="muster-wrapper">
      {error?.generalError && (
        <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
      )}
      <form
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <SignupFormContainer
          backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
          buttonColor={headlineConfig?.buttonColor || '#2979FF'}
          error={error}
          formState={formState}
          isPending={isPending}
          optIn={optIn}
          setError={setError}
          setFormState={setFormState}
          textColor={headlineConfig?.textColor || '#000'}
          submitButtonText={submitButtonText}
        />
      </form>
    </WidgetWrapper>
  );
};
